import { useCallback } from 'react';
import styled, { css } from 'styled-components';

import Label from 'components/core/typography/Label';
import { ExternalLinkIcon, QuestionMarkIcon } from 'components/ui/icons';
import MenuButton, { MenuItem } from 'components/ui/menus/MenuButton';
import { ContactEmails } from 'enums/contactEmails';
import { ResourceCentreUrls } from 'enums/resourceCenterUrls';
import { ElementTestId } from 'enums/testing';
import { isFrCA, translate } from 'utils/intlUtils';

import { BODY_TEXT_TERTIARY } from 'styles/color';
import { BLUE_500, NEUTRAL_0, NEUTRAL_700, SPACE_300 } from 'styles/tokens';

import { MultiNavLinkSubItemCSS, MultiNavLinkSubItemsContainerCSS, NavActionItemBox } from '../shared/CommonNavItems';

const { t } = translate;

const FeedbackNavActionSubItem = styled.div`
  ${MultiNavLinkSubItemCSS}
`;

const FeedbackNavActionSubContainer = styled.div`
  ${MultiNavLinkSubItemsContainerCSS}
`;

const FeedbackNavAction = styled(NavActionItemBox)<{ $isOpen: boolean }>`
  ${({ $isOpen }) => css`
    background: ${$isOpen ? BLUE_500 : NEUTRAL_700};
  `}

  &:hover ${FeedbackNavActionSubContainer} {
    display: block;
  }
`;

export const getContactEmail = () => (isFrCA() ? ContactEmails.FRENCH_SUPPORT : ContactEmails.ENGLISH_SUPPORT);

const email = getContactEmail();

export const getKnowledgeBaseURL = () =>
  isFrCA() ? ResourceCentreUrls.FRENCH_KNOWLEDGE_BASE : ResourceCentreUrls.ENGLISH_KNOWLEDGE_BASE;

const KNOWLEDGE_BASE_URL = getKnowledgeBaseURL();

interface FeedbackMenuNavItemProps {
  title?: string;
}

const ExternalLinkMenuItemIcon = styled(ExternalLinkIcon)`
  margin-left: ${SPACE_300};
`;

const FeedbackMenuNavItem = ({ title }: FeedbackMenuNavItemProps) => {
  const customIcon = useCallback(
    (isOpen: boolean) => (
      <FeedbackNavAction $isOpen={isOpen} data-testid={ElementTestId.FEEDBACK_MENU_BUTTON}>
        {!isOpen && (
          <FeedbackNavActionSubContainer>
            <FeedbackNavActionSubItem>
              <Label>{title}</Label>
            </FeedbackNavActionSubItem>
          </FeedbackNavActionSubContainer>
        )}
        <QuestionMarkIcon color={isOpen ? NEUTRAL_0 : BODY_TEXT_TERTIARY} />
      </FeedbackNavAction>
    ),
    [title]
  );

  return (
    <MenuButton ariaLabel={title ?? t('help')} customIcon={customIcon} position="bottom-left" type="custom">
      <MenuItem icon={null} label={t('contact_us')} onClick={() => globalThis.location.assign(`mailto:${email}`)} />
      <MenuItem
        icon={null}
        label={t('give_feedback')}
        onClick={() => globalThis.location.assign(`mailto:${ContactEmails.FEEDBACK}`)}
      />
      <MenuItem
        icon={<ExternalLinkMenuItemIcon />}
        label={t('knowledge_base')}
        onClick={() => window.open(KNOWLEDGE_BASE_URL, '_blank')}
      />
    </MenuButton>
  );
};

export default FeedbackMenuNavItem;
